// ── src/premiumOutstanding.js ────────────────────────────────────────────────
// WHO STILL OWES PREMIUM.
//
// M13 carries the grower's premium payments as record **P12 "Payment"**, and
// both codes in this book are the insured paying. Sourced from RMA's own RY2026
// Approved Appendix III P12 spec and the ICE **D00060** code list, not inferred:
//
//   00  Paid by Insured
//   02  CAT fees paid after crop termination
//   03  Reversal of CAT fees paid (02)
//   05  Producer Premium Payment from a Livestock Indemnity
//   06  Paid by Insured (Livestock Only)
//
// The book holds only 00 and 06, and the split is exactly what the code names
// say — measured 2026-08-27, `06` appears on **DRP and LRP policies only** (24
// of 24) and `00` on **crop and PRF only** (258 of 258), with no policy
// carrying both. The observation and the published meaning agree, which is what
// makes this a reading rather than a story.
//
// ⚠ WE ALREADY STORED ALL OF IT, IN A TABLE CALLED `loss_notices`, WITH THE
// PAYMENT TYPE IN A COLUMN CALLED `cause_code`. P12 is not the notice of loss —
// CLAUDE.md has recorded that misnaming since 2026-08-19. Nothing read it as
// payments until this module.
//
// ⚠ AND `loss_notices.crop_year` IS NOT THE POLICY'S CROP YEAR. The syncs set it
// to the calendar year of the PAYMENT DATE (P12 f[7]); the M13 FILE year is the
// separate `file_crop_year` column (sql/054). Fall crops are paid the previous
// autumn and next year's policies get paid early, so filtering on `crop_year`
// drops **$1.88M** of payments against CY2026 policies. **JOIN ON `policy_id`**,
// and count a payment only where `file_crop_year` matches the policy's year —
// the join alone does NOT scope the year (see paymentsForPolicy).
//
// ⚠ THERE IS NO DUE DATE IN ANYTHING WE HOLD. `crop_coverage.billing_date` is
// NULL on **all 1,720** CY2026 rows and on every row of every year; PRF's is
// NULL on all 42. The AIP's statement prints a due date and a debt term date and
// neither reaches us. So this module says OUTSTANDING and never "past due",
// "late" or "delinquent" — a grower who simply is not due yet must not appear on
// a list that reads as a collections queue.

export const PAYMENT_TYPE_NAME = {
  "00": "Paid by insured",
  "02": "CAT fees paid after crop termination",
  "03": "Reversal of CAT fees paid",
  "05": "Producer premium paid from a livestock indemnity",
  "06": "Paid by insured (livestock only)",
};

const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const cents = (v) => Math.round((Number(v) || 0) * 100);
const dollars = (c) => c / 100;

// EVERY P12 ROW IS PART OF THE RUNNING TOTAL, WHATEVER ITS CODE. That is RMA's
// own rule, quoted from the P12 spec: *"Payment Amount fields on all P12 records
// for a Policy when summed, must be greater than or equal to zero."* A reversal
// (03) is therefore carried as a negative amount and SUBTRACTS by arithmetic
// rather than by a special case — codes are labels here, not switches.
//
// This is deliberately NOT a whitelist of "codes we recognise". A whitelist
// silently drops a code RMA adds later, and a dropped payment reads as an unpaid
// grower, which is the one direction of this feature that costs a phone call.
// An UNKNOWN code is counted and NAMED, the same convention the claim stage
// codes and the insurance option codes already follow.
export function paymentTypeLabel(code) {
  const c = up(code);
  return PAYMENT_TYPE_NAME[c] || `code ${c || "?"} — meaning not established`;
}
export function isKnownPaymentType(code) {
  return Object.prototype.hasOwnProperty.call(PAYMENT_TYPE_NAME, up(code));
}

// ⚠ THE POLICY_ID JOIN IS NOT SUFFICIENT, AND THIS MODULE USED TO SAY IT WAS.
// Until 2026-09-01 the comment above read "a policy row is one crop year, so the
// year scoping comes free and correct from the policy". The join itself can be
// wrong, so the year does not come free.
//
// Measured over all 334 payments, the partition is EXACT with no exceptions in
// either direction: a payment is attached to the right year **if and only if** a
// policy row for that year exists (99), and to the wrong one if and only if it
// does not (235, $2,741,193). The mechanism is in the syncs — both backfill paths
// index `policyLookup[policy_number]` with a first pass whose own comment says
// "later years overwrite earlier", repaired by a second pass ONLY when a row for
// the backfill year exists. R&H `MP-` numbers carry no crop year, and NAU's
// year-embedded numbers fall through the same base-number fallback. So a payment
// for a crop year this book no longer holds gets stapled to the NEWEST year.
//
// On screen that was $418,510 of "credit balances" on CY2027 policies that were
// never billed. KAST FARMS: MP-0865595 owes $9,422 on CY2026 while an $8,247
// payment out of the CY2025 FILE sat on its CY2027 row.
//
// So a payment is counted only where it can be SHOWN to belong: `file_crop_year`
// is the crop year of the M13 file the P12 came out of — field 2 of the record on
// both AIPs, stored verbatim in `raw_record` since the table was created and
// promoted to a column by sql/054. It is NOT `crop_year`, which is the calendar
// year of the payment DATE (a CY2026 fall crop paid in September 2025 carries
// crop_year 2025 and file_crop_year 2026 — both true, about different things).
//
// REFUSED ON A PROVEN DISAGREEMENT ONLY. Where either year is unknown the payment
// still counts and is flagged `yearUnverified`, because refusing a real payment
// makes a grower who has paid appear on a call list — the one error on this page
// that costs a phone call. Nothing is ever dropped silently: both states are
// returned and the pane prints them.
export function paymentsForPolicy(payments, policyId, policyCropYear) {
  const pid = policyId == null ? null : String(policyId);
  if (pid == null) return [];
  const py = policyCropYear == null || policyCropYear === "" ? null : Number(policyCropYear);
  return (payments || [])
    .filter((p) => p && String(p.policyId ?? p.policy_id) === pid)
    .map((p) => {
      const fy = p.fileCropYear ?? p.file_crop_year;
      const fileYear = fy == null || fy === "" ? null : Number(fy);
      const known = fileYear != null && py != null;
      return {
      fileCropYear: fileYear,
      belongsToYear: known ? fileYear === py : true,
      yearUnverified: !known,
      amount: Number(p.amount) || 0,
      code: up(p.paymentType ?? p.cause_code ?? p.code),
      label: paymentTypeLabel(p.paymentType ?? p.cause_code ?? p.code),
      known: isKnownPaymentType(p.paymentType ?? p.cause_code ?? p.code),
      date: p.paymentDate ?? p.loss_event_date ?? null,
      prePayment: up(p.flag) === "Y",
      };
    });
}

// ⚠ DRP DOES NOT RECONCILE TO A POLICY YEAR, AND IS REFUSED RATHER THAN GUESSED.
// Measured 2026-08-27 over all six DRP policies carrying a payment: only ONE
// matches its producer premium. Sparta Farms LP paid **$940,800 against a
// $666,265** CY2025 premium and **$420,210 against a $530,322** CY2026 premium —
// **both dated the same day**, so the money plainly does not divide the way the
// policy rows do. DRP endorsements are quarterly and its crop year is derived
// from the endorsement effective date (July+ rolls forward), so a payment cannot
// be attributed to a policy year from anything we hold.
//
// Presented as an outstanding balance that would read "Sparta is $110,112 short
// on CY2026" and "$274,535 in credit on CY2025" — two invented facts about the
// largest dairy in the book. It is listed, subtotalled and EXCLUDED from the
// figure Kevin acts on, never silently dropped and never quietly counted.
export const RECONCILABLE_PRODUCTS = { crop: true, prf: true, lrp: true, drp: false };
export function productReconciles(policyType) {
  return RECONCILABLE_PRODUCTS[String(policyType || "crop").toLowerCase()] === true;
}

// What each product's BILLED figure rests on, printed on screen so a reader can
// tell the sourced half from the modelled half without reading this file.
export const BILLED_BASIS = {
  crop: "producer premium + administrative fees − claim credits",
  prf:  "producer premium (no fee modelled)",
  lrp:  "producer premium (no fee modelled)",
  drp:  "producer premium (not reconcilable — see note)",
};

// PRF AND LRP CARRY NO MODELLED FEE, AND THAT IS MEASURED RATHER THAN ASSUMED.
// Where those policies were paid, the payment equals the producer premium
// exactly — PRF 21 of 26, LRP 14 of 18 — so adding an administrative fee would
// invent a debt on a farm that has paid in full. The crop fee is modelled only
// because the crop side has the AIP's own F-credit rows to validate it against
// (12 of 13 fully-credited policies reproduce this module's fee-line count).
// Do not "make it consistent" by charging PRF and LRP a fee too.
export const FEE_MODELLED = { crop: true, prf: false, lrp: false, drp: false };

// One policy's bill against one policy's payments.
//
// `billed` is supplied by the caller because the crop half comes from
// src/billing.js — the SAME rule the farmer card's billing panel renders — and
// duplicating it here is how the two surfaces come to disagree about what one
// farm owes. This repo has paid for that four times over.
export function policyOutstanding({ policyId, policyNumber, policyType, farmerId, farmerName,
                                    cropYear, aip, agent, billed, payments }) {
  const type = String(policyType || "crop").toLowerCase();
  const rows = paymentsForPolicy(payments, policyId, cropYear);
  // Only what belongs to THIS crop year is money against THIS bill.
  const mine = rows.filter((p) => p.belongsToYear);
  const foreign = rows.filter((p) => !p.belongsToYear);
  const paidC = mine.reduce((s, p) => s + cents(p.amount), 0);
  const billedC = cents(billed);
  const outC = billedC - paidC;
  const reconciles = productReconciles(type);
  return {
    policyId, policyNumber: policyNumber || null, policyType: type,
    farmerId: farmerId ?? null, farmerName: farmerName || null,
    cropYear: cropYear ?? null, aip: aip || null,
    // ⚠ THE AGENT IS A PROPERTY OF THE POLICY, NEVER OF THE FARM. Measured
    // 2026-08-27: 19 of 280 customers carry policies written by MORE THAN ONE
    // agent, and 120 carry at least one policy no agent resolves for. Stamping a
    // farm with one agent name would be wrong on 19 farms and silently wrong —
    // which is why `agentsOf` returns a SET and the filter narrows the MONEY
    // rather than just the list.
    agent: agent || null,
    billed: dollars(billedC),
    paid: dollars(paidC),
    // NOT clamped at zero in either direction. A credit balance means the grower
    // paid more than we billed, which is either a real credit or a defect in our
    // billed figure — both want looking at, and rounding it to $0 hides both.
    outstanding: dollars(outC),
    payments: mine,
    paymentCount: mine.length,
    lastPaymentDate: mine.reduce((d, p) => (p.date && (!d || p.date > d) ? p.date : d), null),
    // Refused, never dropped. These are real payments for a crop year this book
    // holds no policy row for; they are counted, subtotalled and shown.
    foreignYearPayments: foreign,
    foreignYearAmount: dollars(foreign.reduce((s, p) => s + cents(p.amount), 0)),
    unverifiedYearCount: mine.filter((p) => p.yearUnverified).length,
    // Three states, because they are three different facts and a single
    // "unpaid" flag would merge the first two.
    //   unpaid   — nothing has been received against this policy
    //   partial  — some money has arrived and a balance remains
    //   settled  — the balance is zero
    //   credit   — more was received than we billed
    state: !reconciles ? "not_reconcilable"
         : outC === 0 ? "settled"
         : outC < 0 ? "credit"
         : paidC === 0 ? "unpaid" : "partial",
    reconciles,
    basis: BILLED_BASIS[type] || BILLED_BASIS.crop,
    feeModelled: FEE_MODELLED[type] === true,
    unknownCodes: [...new Set(mine.filter((p) => !p.known).map((p) => p.code))],
  };
}

// The label for a policy no agent resolves for. NAMED, never blank and never
// folded into somebody's book — `canonAgent` returns null for a prior or
// external agent (ERIC D COOK, WILLIAM KULLOT and ALEX LARKIN are all in the
// live data), and "we did not write this" is a different fact from "nobody owes
// anything on it".
export const UNATTRIBUTED = "Unattributed";
export const agentLabel = (a) => a || UNATTRIBUTED;

// Every agent that wrote a policy on this farm. A SET, because 19 of 280
// customers span more than one.
export function agentsOf(policyRows) {
  return [...new Set((policyRows || []).map((r) => agentLabel(r && r.agent)))].sort();
}

// ⚠ NARROWING BY AGENT NARROWS THE MONEY, NOT JUST THE LIST. Sparta Farms owes
// $185,183 across five policies; if one is Kevin's and four are Mike's, showing
// Kevin the farm's whole balance overstates his book by everything Mike wrote.
// So the filter drops the POLICIES that are not his and the farmer row is REBUILT
// from what is left — which is why this returns rows to be re-rolled rather than
// a boolean for a `.filter()`.
export function narrowToAgent(policyRows, agent) {
  if (!agent) return policyRows || [];
  return (policyRows || []).filter((r) => agentLabel(r && r.agent) === agent);
}

// ⚠ THE CROP YEAR IS THE POLICY'S, AND FILTERING IT MUST NEVER REACH THE
// PAYMENTS. `loss_notices.crop_year` is the year of the payment DATE, not the
// policy's (the file year is `file_crop_year`) — a fall crop is paid the
// previous autumn and next year's policies get paid early. Narrowing
// payments by their own year would drop **$1.88M** and every farm it dropped
// would reappear as unpaid, which is the single defect this whole module exists
// to avoid.
//
// Narrowing POLICIES is safe and is a different statement: a policy row is one
// crop year, so its payments — already joined on `policy_id` — come with it.
export function narrowToYear(policyRows, year) {
  if (year == null || year === "") return policyRows || [];
  return (policyRows || []).filter((r) => Number(r && r.cropYear) === Number(year));
}

// Every crop year with a policy on this page, newest first — read from the data
// so a year the book starts carrying appears on its own.
export function yearsOf(policyRows) {
  return [...new Set((policyRows || []).map((r) => Number(r && r.cropYear)).filter(Number.isFinite))]
    .sort((a, b) => b - a);
}

// Roll the policy rows up per farmer, and keep the unreconcilable ones OUT of
// the headline while still returning them to be shown.
export function rollUpByFarmer(policyRows) {
  const byFarmer = new Map();
  (policyRows || []).forEach((r) => {
    if (!r) return;
    const key = String(r.farmerId ?? r.farmerName ?? "?");
    if (!byFarmer.has(key)) {
      byFarmer.set(key, {
        farmerId: r.farmerId ?? null, farmerName: r.farmerName || "—",
        billed: 0, paid: 0, outstanding: 0, owed: 0, credit: 0, policies: [],
        agents: [], years: [],
        foreignYearAmount: 0, foreignYearCount: 0,
        excludedBilled: 0, excludedPaid: 0, excludedPolicies: [],
      });
    }
    const g = byFarmer.get(key);
    // Counted on BOTH sides of the reconcilable split — a payment we refused to
    // attribute is a fact about the book whether or not we price the product.
    g.foreignYearAmount += r.foreignYearAmount || 0;
    g.foreignYearCount  += (r.foreignYearPayments || []).length;
    if (r.reconciles) {
      g.billed += r.billed; g.paid += r.paid; g.outstanding += r.outstanding;
      // ⚠ OWED AND CREDIT ARE KEPT APART, NEVER NETTED INTO ONE NUMBER.
      // Measured on the live book 2026-08-27: Hansen Farms carries $5,461
      // outstanding on MP-0906029 **CY2026** and an $8,373 payment stamped
      // against **CY2027** of the same policy number — a fall crop paid the
      // previous autumn. Netted, that farm reads "$2,910 in credit" and drops
      // off a call list it belongs on. They are different bills; the sum of the
      // positives is what somebody actually still owes.
      g.owed += Math.max(0, r.outstanding);
      g.credit += Math.min(0, r.outstanding);
      g.policies.push(r);
    } else {
      g.excludedBilled += r.billed; g.excludedPaid += r.paid;
      g.excludedPolicies.push(r);
    }
  });
  return [...byFarmer.values()].map((g) => ({
    ...g,
    billed: dollars(cents(g.billed)), paid: dollars(cents(g.paid)),
    outstanding: dollars(cents(g.outstanding)),
    // Over the reconcilable policies AND the excluded ones — a DRP policy this
    // page refuses to price is still a policy somebody wrote, and hiding it from
    // the agent lens would make a farm vanish from its own agent's list.
    agents: agentsOf([...g.policies, ...g.excludedPolicies]),
    years: yearsOf([...g.policies, ...g.excludedPolicies]),
    foreignYearAmount: dollars(cents(g.foreignYearAmount)),
    foreignYearCount: g.foreignYearCount,
    owed: dollars(cents(g.owed)), credit: dollars(cents(g.credit)),
    excludedBilled: dollars(cents(g.excludedBilled)),
    excludedPaid: dollars(cents(g.excludedPaid)),
    // The state follows what is OWED, not the net — a farm owing on one year
    // and in credit on another still owes.
    state: g.policies.length === 0 ? "not_reconcilable"
         : g.owed > 0 ? (g.paid === 0 ? "unpaid" : "partial")
         : g.credit < 0 ? "credit" : "settled",
  })).sort((a, b) => b.owed - a.owed || String(a.farmerName).localeCompare(String(b.farmerName)));
}

// The book figure. Sums in cents and reports what it left out, because a total
// that quietly absorbs the products it cannot reconcile is the reason nobody
// trusts the second one.
export function outstandingTotals(farmerRows) {
  const rows = farmerRows || [];
  const sum = (f) => dollars(rows.reduce((s, r) => s + cents(f(r) || 0), 0));
  return {
    farmers: rows.length,
    farmersOwing: rows.filter((r) => (r.owed || 0) > 0).length,
    billed: sum((r) => r.billed),
    paid: sum((r) => r.paid),
    // What is owed, and what is in credit, reported as two figures. A netted
    // total lets one farm's credit cancel another's debt, and then the number
    // answers neither question.
    outstanding: sum((r) => r.owed),
    credits: sum((r) => r.credit),
    net: sum((r) => r.outstanding),
    excludedBilled: sum((r) => r.excludedBilled),
    excludedPaid: sum((r) => r.excludedPaid),
    excludedPolicies: rows.reduce((s, r) => s + (r.excludedPolicies || []).length, 0),
    // The payments this page refused to attribute to any year it can price.
    foreignYearAmount: sum((r) => r.foreignYearAmount),
    foreignYearCount: rows.reduce((s, r) => s + (r.foreignYearCount || 0), 0),
  };
}

// ── Book assembly ───────────────────────────────────────────────────────────
// Deliberately takes `billedByPolicy` rather than importing src/billing.js. The
// crop bill is that module's job and this one must not grow a second copy of it;
// the caller passes what the farmer card already renders, so the two surfaces
// cannot disagree about what one farm owes. It also keeps this file import-free,
// which is what lets the checker load and drive the SHIPPED source directly.

// Producer premium per policy for the products that are not MPCI. No fee is
// added — measured, not assumed: PRF payments equal producer premium exactly on
// 21 of 26 and LRP on 14 of 18, so a modelled fee would invent a debt on a farm
// that has paid in full.
export function nonCropBilledByPolicy({ prfRows, lrpRows, drpRows }) {
  const out = new Map();
  const add = (pid, v) => {
    if (pid == null) return;
    const k = String(pid);
    out.set(k, (out.get(k) || 0) + (Number(v) || 0));
  };
  (prfRows || []).forEach((r) => add(r.policy_id ?? r.policyId,
    r.net_premium ?? r.netPremium ?? ((Number(r.total_premium ?? r.totalPremium) || 0) - (Number(r.subsidy) || 0))));
  (lrpRows || []).forEach((r) => add(r.policy_id ?? r.policyId, r.producer_premium ?? r.producerPremium));
  (drpRows || []).forEach((r) => add(r.policy_id ?? r.policyId, r.producer_premium ?? r.producerPremium));
  return out;
}

// Every policy that has a bill or a payment, as one row apiece.
//
// A policy with NEITHER is not listed at all — a forward-year shell with no
// premium keyed yet is not a grower who owes nothing, it is a policy that has
// not been billed, and putting it on a collections list as "settled" is a claim
// about money that was never asked for.
export function buildBookOutstanding({ policies, billedByPolicy, payments, farmerNameById, cropYears, alsoList, agentFor }) {
  const years = cropYears && cropYears.length ? new Set(cropYears.map(Number)) : null;
  const paidPolicyIds = new Set((payments || []).map((p) => String(p.policyId ?? p.policy_id)));
  // A policy whose bill a CLAIM CREDIT reduced to zero WAS billed, and reads as
  // settled — which is a different fact from a forward-year shell that was never
  // billed at all. Measured: Sparta's NY-944-1020762-26 is billed $22,000 and
  // credited $22,000, and without this it vanished from the page entirely.
  const listAnyway = new Set((alsoList || []).map(String));
  return (policies || [])
    .filter((p) => p && p.id != null)
    .filter((p) => !years || years.has(Number(p.crop_year ?? p.cropYear)))
    .map((p) => {
      const pid = String(p.id);
      const billed = Number(billedByPolicy && billedByPolicy.get ? billedByPolicy.get(pid) : 0) || 0;
      if (!billed && !paidPolicyIds.has(pid) && !listAnyway.has(pid)) return null;
      return policyOutstanding({
        policyId: p.id,
        policyNumber: p.policy_number ?? p.policyNumber ?? null,
        policyType: p.policy_type ?? p.policyType ?? "crop",
        farmerId: p.farmer_id ?? p.farmerId ?? null,
        farmerName: (farmerNameById && farmerNameById.get
          ? farmerNameById.get(String(p.farmer_id ?? p.farmerId))
          : null) || null,
        cropYear: p.crop_year ?? p.cropYear ?? null,
        aip: p.aip || null,
        // Resolved by the SAME rule Premium Analytics uses — `paAgentResolver`
        // in App.jsx, passed in rather than reimplemented. No resolver yields
        // null (unattributed), never a guess.
        agent: typeof agentFor === "function"
          ? agentFor(p.policy_number ?? p.policyNumber, p.agent_name_override, p.agent_name)
          : null,
        billed,
        payments,
      });
    })
    .filter(Boolean);
}
